import * as THREE from 'three';
import { HazardPhase, HazardType } from '@lucky-vr/common';
import { emissiveMaterial, paintedMetalMaterial } from '../core/MaterialLibrary.js';

interface HazardVisual {
  group: THREE.Group;
  ring: THREE.Mesh;
  symbol: THREE.Group;
  hazardType: HazardType;
  phase: HazardPhase;
  time: number;
  phaseTime: number;
  phaseDuration: number;
  throwSide: number;
}

export interface HazardVisualState {
  hazardType: HazardType;
  phase: HazardPhase;
  position: THREE.Vector3;
  quaternion: THREE.Quaternion;
  radius: number;
  phaseRemaining: number;
}

/** Road-space hazard telegraphs. Geometry stays simple so silhouettes remain clear in VR. */
export class HazardManager {
  private visuals = new Map<string, HazardVisual>();

  constructor(private parent: THREE.Group) {}

  sync(states: Map<string, HazardVisualState>): void {
    for (const [id, state] of states) {
      let visual = this.visuals.get(id);
      if (!visual) {
        visual = this.add(id, state);
        this.visuals.set(id, visual);
      }
      visual.group.position.copy(state.position);
      visual.group.quaternion.copy(state.quaternion);
      if (visual.phase !== state.phase) {
        visual.phase = state.phase;
        visual.phaseTime = 0;
        visual.phaseDuration = Math.max(0.05, state.phaseRemaining);
      }
      const committed = state.phase === HazardPhase.Committed || state.phase === HazardPhase.Active;
      (visual.ring.material as THREE.MeshBasicMaterial).color.setHex(committed ? 0xff3322 : 0xffb020);
      visual.symbol.visible = true;
    }
    for (const id of [...this.visuals.keys()]) {
      if (!states.has(id)) this.remove(id);
    }
  }

  update(dt: number): void {
    for (const visual of this.visuals.values()) {
      visual.time += dt;
      visual.phaseTime += dt;
      const progress = THREE.MathUtils.clamp(visual.phaseTime / visual.phaseDuration, 0, 1);
      const pulse = 0.94 + Math.sin(visual.time * (visual.phase === HazardPhase.Warning ? 7 : 14)) * 0.06;
      const phaseScale = visual.phase === HazardPhase.Active ? 1 + progress * 0.48
        : visual.phase === HazardPhase.Recovery ? 1.48 - progress * 0.18
        : 1;
      visual.ring.scale.setScalar(pulse * phaseScale);
      visual.ring.rotation.z += dt * 0.65;
      const material = visual.ring.material as THREE.MeshBasicMaterial;
      material.opacity = visual.phase === HazardPhase.Recovery
        ? 0.32 * (1 - progress)
        : visual.phase === HazardPhase.Active
          ? 0.72 * (1 - progress * 0.65)
          : 0.46 + pulse * 0.24;
      this.animateSymbol(visual, dt, progress);
    }
  }

  clear(): void {
    for (const id of [...this.visuals.keys()]) this.remove(id);
  }

  private add(id: string, state: HazardVisualState): HazardVisual {
    const group = new THREE.Group();
    const ring = new THREE.Mesh(
      new THREE.RingGeometry(Math.max(0.5, state.radius * 0.65), state.radius, 24),
      new THREE.MeshBasicMaterial({
        color: 0xffb020,
        transparent: true,
        opacity: 0.7,
        side: THREE.DoubleSide,
        depthWrite: false,
        toneMapped: false,
      }),
    );
    ring.rotation.x = -Math.PI / 2;
    ring.position.y = 0.08;
    group.add(ring);

    const symbol = this.buildSymbol(state.hazardType);
    group.add(symbol);

    group.position.copy(state.position);
    group.quaternion.copy(state.quaternion);
    this.parent.add(group);
    const hash = [...id].reduce((value, char) => ((value * 31) + char.charCodeAt(0)) | 0, 7);
    return {
      group,
      ring,
      symbol,
      hazardType: state.hazardType,
      phase: state.phase,
      time: 0,
      phaseTime: 0,
      phaseDuration: Math.max(0.05, state.phaseRemaining),
      throwSide: hash % 2 === 0 ? 1 : -1,
    };
  }

  private buildSymbol(type: HazardType): THREE.Group {
    const symbol = new THREE.Group();
    const basic = (color: number, opacity = 1): THREE.MeshBasicMaterial => new THREE.MeshBasicMaterial({
      color,
      transparent: opacity < 1,
      opacity,
      depthWrite: opacity >= 1,
      toneMapped: false,
    });
    const solid = (color: number, roughness = 0.7): THREE.MeshStandardMaterial =>
      new THREE.MeshStandardMaterial({ color, roughness, metalness: 0.08 });

    if (type === HazardType.CargoDrop) {
      for (const [x, y, z] of [[-0.8, 0, 0.2], [0.7, 0.45, -0.3], [0.1, 0.8, 0.55]]) {
        const crate = new THREE.Mesh(new THREE.BoxGeometry(0.9, 0.9, 0.9), paintedMetalMaterial(0xa9561f, 0.5));
        crate.position.set(x, y, z);
        crate.rotation.set(x * 0.3, z * 0.5, y * 0.2);
        symbol.add(crate);
      }
    } else if (type === HazardType.Rockfall) {
      for (const [x, y, z] of [[-0.9, 0.2, 0], [0.6, 0.5, -0.4], [0.2, 1, 0.5]]) {
        const rock = new THREE.Mesh(new THREE.DodecahedronGeometry(0.65, 0), solid(0x6a5a4d, 0.94));
        rock.position.set(x, y, z);
        symbol.add(rock);
      }
    } else if (type === HazardType.TrafficCrossing || type === HazardType.LaneStrike) {
      const body = new THREE.Mesh(
        new THREE.BoxGeometry(2.2, 0.75, 4.2),
        paintedMetalMaterial(type === HazardType.LaneStrike ? 0xa81717 : 0x9da2aa, 0.38),
      );
      body.position.y = 0.65;
      symbol.add(body);
      for (const x of [-0.7, 0.7]) {
        const light = new THREE.Mesh(new THREE.BoxGeometry(0.35, 0.25, 0.08), emissiveMaterial(0xffdd75, 3.8));
        light.position.set(x, 0.7, -2.14);
        symbol.add(light);
      }
      symbol.rotation.y = Math.PI / 2;
    } else if (type === HazardType.TunnelFire || type === HazardType.SwampEruption) {
      for (const [x, scale] of [[-0.75, 0.75], [0, 1.2], [0.75, 0.9]]) {
        const flame = new THREE.Mesh(
          new THREE.ConeGeometry(0.55 * scale, 2.3 * scale, 7),
          basic(type === HazardType.SwampEruption ? 0x8dff55 : 0xff5a22, 0.78),
        );
        flame.position.set(x, 1.1 * scale, 0);
        symbol.add(flame);
      }
    } else {
      // Dust bursts stay low and broad; no vertical beam language.
      for (const [x, y, scale] of [[-0.9, 0.6, 1], [0.25, 0.85, 1.35], [1, 0.5, 0.8]]) {
        const dust = new THREE.Mesh(
          new THREE.SphereGeometry(scale, 10, 7),
          basic(0xc58b50, 0.32),
        );
        dust.position.set(x, y, 0);
        symbol.add(dust);
      }
    }
    return symbol;
  }

  private animateSymbol(visual: HazardVisual, dt: number, progress: number): void {
    const ease = progress * progress * (3 - 2 * progress);
    if (visual.hazardType === HazardType.CargoDrop || visual.hazardType === HazardType.Rockfall) {
      const cargo = visual.hazardType === HazardType.CargoDrop;
      const side = visual.throwSide;
      const startX = cargo ? side * 1.25 : side * 4.2;
      const startY = cargo ? 5.4 : 2.8;
      const startZ = cargo ? -0.8 : -3.4;
      visual.symbol.scale.setScalar(1);

      if (visual.phase === HazardPhase.Warning) {
        visual.symbol.position.set(
          THREE.MathUtils.lerp(startX * 1.14, startX, ease),
          startY + Math.sin(visual.time * 5) * 0.08,
          THREE.MathUtils.lerp(startZ - 0.7, startZ, ease),
        );
        visual.symbol.rotation.y += dt * 0.55;
      } else if (visual.phase === HazardPhase.Committed) {
        const arc = Math.sin(progress * Math.PI) * (cargo ? 0.7 : 2.5);
        visual.symbol.position.set(
          THREE.MathUtils.lerp(startX, 0, ease),
          THREE.MathUtils.lerp(startY, 0.24, ease) + arc,
          THREE.MathUtils.lerp(startZ, 0, ease),
        );
        visual.symbol.rotation.x += dt * (cargo ? 2.8 : 4.4);
        visual.symbol.rotation.y += dt * (cargo ? 2.2 : 3.5) * side;
        visual.symbol.rotation.z += dt * 1.7 * side;
      } else if (visual.phase === HazardPhase.Active) {
        const bounce = Math.abs(Math.sin(progress * Math.PI * 3)) * Math.exp(-4.2 * progress);
        visual.symbol.position.set(0, 0.24 + bounce * (cargo ? 0.48 : 0.72), 0);
        visual.symbol.scale.setScalar(1 + Math.exp(-5 * progress) * 0.14);
        visual.symbol.rotation.x += dt * 2.2 * (1 - progress);
        visual.symbol.rotation.z += dt * 1.4 * side * (1 - progress);
      } else {
        visual.symbol.position.set(0, 0.24, 0);
        // Once landed, cargo and rocks stay lodged at full readable size until
        // the vehicle has physically driven past their fixed road position.
        visual.symbol.scale.setScalar(1);
      }
    } else if (visual.hazardType === HazardType.TrafficCrossing || visual.hazardType === HazardType.LaneStrike) {
      const side = visual.throwSide;
      if (visual.phase === HazardPhase.Warning) visual.symbol.position.x = side * (5.5 - ease * 0.7);
      else if (visual.phase === HazardPhase.Committed) visual.symbol.position.x = THREE.MathUtils.lerp(side * 4.8, 0, ease);
      else if (visual.phase === HazardPhase.Active) visual.symbol.position.x = THREE.MathUtils.lerp(0, -side * 4.8, ease);
      else visual.symbol.position.x = THREE.MathUtils.lerp(-side * 4.8, -side * 6.2, ease);
    } else if (visual.hazardType === HazardType.TunnelFire || visual.hazardType === HazardType.SwampEruption) {
      const base = visual.phase === HazardPhase.Warning ? THREE.MathUtils.lerp(0.28, 0.7, ease)
        : visual.phase === HazardPhase.Committed ? THREE.MathUtils.lerp(0.7, 1.1, ease)
        : visual.phase === HazardPhase.Active ? 1.1
        : THREE.MathUtils.lerp(1.1, 0.08, ease);
      visual.symbol.scale.set(1, base * (0.84 + Math.abs(Math.sin(visual.time * 9)) * 0.28), 1);
    } else {
      const dustScale = visual.phase === HazardPhase.Warning ? THREE.MathUtils.lerp(0.45, 0.8, ease)
        : visual.phase === HazardPhase.Committed ? THREE.MathUtils.lerp(0.8, 1.25, ease)
        : visual.phase === HazardPhase.Active ? THREE.MathUtils.lerp(1.25, 1.65, ease)
        : THREE.MathUtils.lerp(1.65, 0.2, ease);
      visual.symbol.scale.setScalar(dustScale);
      visual.symbol.rotation.y += dt * 0.35;
    }
  }

  private remove(id: string): void {
    const visual = this.visuals.get(id);
    if (!visual) return;
    this.parent.remove(visual.group);
    visual.group.traverse((object) => {
      if (!(object instanceof THREE.Mesh)) return;
      object.geometry.dispose();
      const materials = Array.isArray(object.material) ? object.material : [object.material];
      materials.forEach((material) => material.dispose());
    });
    this.visuals.delete(id);
  }
}
